import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Feather, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BACKEND_URL } from '../../utils/api';
import { WaxSeal } from './WaxSeal';

/* ═══════════════════════════════════════════════════════════════════
   ROYAL QUOTE BOOK — black & gold leather-bound
   • Page 0: Cover (empire name + "The Royal Quotes")
   • Page 1: Compose-new (always opens here for new quote)
   • Page 2..N: Previously sealed quotes (newest first)
   • Tap left/right or swipe to flip pages, smooth 3D flip
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#D4AF37';
const GOLD_LIGHT = '#F5D97A';
const INK = '#1a1208';
const PAGE_BG = '#f6e8c8';

/* Single page chrome — used by every page type */
const PageFrame = ({ children, side = 'right' }) => (
  <div
    className="absolute inset-0 flex flex-col"
    style={{
      background:
        'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
      borderRadius: side === 'right' ? '4px 14px 14px 4px' : '14px 4px 4px 14px',
      boxShadow:
        'inset 0 0 70px rgba(168,107,31,0.18), inset 0 0 12px rgba(82,46,8,0.5), 0 8px 28px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    }}
  >
    {/* Outer gold border */}
    <div
      className="absolute inset-2 pointer-events-none"
      style={{ border: `2px solid ${GOLD}`, borderRadius: 6 }}
    />
    {/* Inner thin gold border */}
    <div
      className="absolute inset-3.5 pointer-events-none"
      style={{ border: `1px solid rgba(168,107,31,0.5)`, borderRadius: 4 }}
    />
    {/* Subtle corner flourishes */}
    {['1.5rem', 'calc(100% - 1.5rem)'].map((y) =>
      ['1.5rem', 'calc(100% - 1.5rem)'].map((x) => (
        <span
          key={`${x}-${y}`}
          aria-hidden
          style={{
            position: 'absolute', left: x, top: y,
            width: 14, height: 14,
            transform: 'translate(-50%, -50%) rotate(45deg)',
            border: `1px solid ${GOLD}`,
            background: 'rgba(212,175,55,0.18)',
            pointerEvents: 'none',
          }}
        />
      ))
    )}
    {/* Spine shadow on the bound side */}
    <div
      aria-hidden
      style={{
        position: 'absolute', top: 0, bottom: 0,
        [side === 'right' ? 'left' : 'right']: 0,
        width: 16,
        background:
          side === 'right'
            ? 'linear-gradient(90deg, rgba(0,0,0,0.45), transparent)'
            : 'linear-gradient(270deg, rgba(0,0,0,0.45), transparent)',
        pointerEvents: 'none',
      }}
    />
    {children}
  </div>
);

/* Cover page — deep oxblood-leather BOOK COVER (not a parchment inner page).
   Custom leather chrome with embossed gold frame + corner medallions, gold
   dragon-seal motif behind the portrait, and a hardback spine shadow. */
const CoverPage = ({ empireName, profilePicture, readOnly = false }) => {
  const initial = (empireName || 'A')[0].toUpperCase();
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        // Deep oxblood-leather gradient (the "outside" of the book, not parchment).
        background:
          'radial-gradient(ellipse at 50% 38%, #6e1620 0%, #4a0a14 45%, #2a040a 100%)',
        borderRadius: '4px 14px 14px 4px',
        boxShadow:
          'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 18px rgba(0,0,0,0.7), 0 12px 32px rgba(0,0,0,0.7)',
      }}
    >
      {/* Layer 1 — leather grain noise (CSS-only fractal feel via repeating radials). */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.04) 0%, transparent 30%),' +
            'radial-gradient(circle at 78% 28%, rgba(0,0,0,0.18) 0%, transparent 28%),' +
            'radial-gradient(circle at 30% 78%, rgba(0,0,0,0.22) 0%, transparent 30%),' +
            'radial-gradient(circle at 82% 76%, rgba(255,225,180,0.05) 0%, transparent 32%)',
          mixBlendMode: 'overlay',
          opacity: 0.85,
        }}
      />
      {/* Layer 2 — subtle vignette darkening edges */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%)',
        }}
      />

      {/* Embossed gold frame stack */}
      <div
        aria-hidden
        className="absolute inset-3 pointer-events-none"
        style={{
          border: `2.5px solid ${GOLD}`,
          borderRadius: 8,
          boxShadow:
            'inset 0 0 0 1px rgba(255,241,184,0.4), 0 0 14px rgba(212,175,55,0.35)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-5 pointer-events-none"
        style={{
          border: '1px solid rgba(212,175,55,0.55)',
          borderRadius: 5,
        }}
      />

      {/* Corner medallions — gold rosettes at all 4 corners */}
      {[
        { top: 14, left: 14 },
        { top: 14, right: 14 },
        { bottom: 14, left: 14 },
        { bottom: 14, right: 14 },
      ].map((pos, i) => (
        <div
          key={i}
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            ...pos,
            width: 22, height: 22,
            transform: 'rotate(45deg)',
            background:
              'linear-gradient(135deg, #F5E6A0 0%, #D4AF37 45%, #8B6B1F 100%)',
            border: '1px solid rgba(60,40,8,0.6)',
            boxShadow: '0 0 8px rgba(212,175,55,0.55), inset 0 0 4px rgba(255,255,255,0.35)',
          }}
        />
      ))}

      {/* Dragon-seal watermark behind everything (tinted gold, low opacity) */}
      <img
        src="/icons/empire_seal.png"
        alt=""
        aria-hidden
        draggable={false}
        className="absolute pointer-events-none select-none"
        style={{
          width: '70%', height: 'auto',
          left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 0.07,
          filter: 'sepia(1) hue-rotate(-15deg) saturate(2.5) brightness(1.4)',
          mixBlendMode: 'screen',
        }}
      />

      {/* Spine shadow on the bound (left) edge */}
      <div
        aria-hidden
        style={{
          position: 'absolute', top: 0, bottom: 0, right: 0,
          width: 18,
          background: 'linear-gradient(270deg, rgba(0,0,0,0.6), transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* ── Cover content ────────────────────────────────────────────── */}
      {/* Title */}
      <p
        className="font-['Cormorant_Garamond'] uppercase tracking-[0.42em] text-[11px] mb-6 relative z-10"
        style={{
          color: '#F5E6A0',
          textShadow: '0 1px 0 rgba(0,0,0,0.45)',
        }}
      >
        ~ Volume of an Empire ~
      </p>

      {/* Sovereign portrait */}
      <div className="relative mb-6 z-10" style={{ width: 110, height: 110 }}>
        {/* Outer gold halo */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(245,217,122,0.55) 0%, rgba(212,175,55,0) 65%)',
            filter: 'blur(8px)',
            transform: 'scale(1.35)',
          }}
        />
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            border: '3px solid #D4AF37',
            boxShadow:
              'inset 0 0 0 2px rgba(255,241,184,0.7),' +
              '0 0 18px rgba(212,175,55,0.55),' +
              '0 6px 18px rgba(0,0,0,0.55)',
            background: profilePicture
              ? '#1a0608'
              : 'radial-gradient(circle at 50% 32%, #f6e6b6, #c69b3f 70%, #6b4612 100%)',
          }}
        >
          {profilePicture ? (
            <img
              src={profilePicture}
              alt={empireName}
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 700,
                fontSize: 50,
                fontStyle: 'italic',
                color: '#3a2410',
                textShadow: '0 1px 0 rgba(255,241,184,0.6)',
              }}
            >
              {initial}
            </div>
          )}
        </div>
      </div>

      {/* Empire name — embossed gold foil */}
      <h1
        className="font-['Cormorant_Garamond'] font-bold leading-none relative z-10"
        style={{
          fontSize: 'clamp(40px, 12vw, 68px)',
          letterSpacing: '0.06em',
          background: 'linear-gradient(180deg, #FFF1B8 0%, #F5D97A 35%, #C69B3F 75%, #7A5418 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          textShadow: '0 2px 0 rgba(0,0,0,0.4), 0 0 22px rgba(212,175,55,0.35)',
          filter: 'drop-shadow(0 1px 0 rgba(60,30,8,0.7))',
        }}
      >
        {empireName}
      </h1>

      <div
        className="my-5 w-32 h-[1.5px] relative z-10"
        style={{
          background: `linear-gradient(90deg, transparent, ${GOLD_LIGHT}, transparent)`,
          boxShadow: '0 0 8px rgba(245,217,122,0.5)',
        }}
      />

      {/* Subtitle — book title (italic gold) */}
      <h2
        className="font-['Cormorant_Garamond'] italic font-light relative z-10"
        style={{
          color: '#F5E6A0',
          fontSize: 'clamp(22px, 6vw, 30px)',
          letterSpacing: '0.08em',
          textShadow: '0 1px 0 rgba(0,0,0,0.5)',
        }}
      >
        The Royal Quotes
      </h2>

      {/* Decorative quill */}
      <div className="mt-10 opacity-80 relative z-10">
        <Feather size={32} style={{ color: GOLD_LIGHT, filter: 'drop-shadow(0 0 6px rgba(245,217,122,0.5))' }} />
      </div>

      {/* Footer */}
      <p
        className="absolute bottom-7 left-0 right-0 text-center font-['Cormorant_Garamond'] italic text-[11px] z-10"
        style={{ color: 'rgba(245,230,160,0.65)' }}
      >
        {readOnly ? '— turn the page to read the decrees —' : '— turn the page to inscribe a decree —'}
      </p>
    </div>
  );
};

/* Compose-new page (always page 1 — fresh on every open) */
const ComposePage = ({ empireName, onSubmit, submitting, value, onChange, lesson, onLessonChange }) => (
  <PageFrame>
    <div className="relative flex-1 flex flex-col px-5 sm:px-7 pt-7 pb-5">
      <p
        className="font-['Cormorant_Garamond'] uppercase tracking-[0.32em] text-[10px] text-center"
        style={{ color: 'rgba(82,46,8,0.7)' }}
      >
        ~ Inscribe a Decree ~
      </p>
      <div
        className="mx-auto mt-2 mb-4 w-20 h-[1px]"
        style={{ background: GOLD }}
      />

      <input
        type="text"
        value={lesson}
        onChange={(e) => onLessonChange(e.target.value)}
        placeholder="Lesson title (optional)"
        maxLength={80}
        disabled={submitting}
        className="w-full bg-transparent border-0 border-b border-amber-900/40 focus:border-amber-900 outline-none px-1 py-1.5 text-center font-['Cormorant_Garamond'] font-semibold text-xl placeholder-amber-900/40"
        style={{ color: INK }}
        data-testid="quote-lesson-input"
      />

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Write your timeless quote..."
        maxLength={500}
        rows={7}
        disabled={submitting}
        className="mt-4 flex-1 w-full bg-transparent border-none outline-none resize-none text-center font-['Cormorant_Garamond'] italic px-2 leading-relaxed placeholder-amber-900/35"
        style={{ color: INK, fontSize: 'clamp(15px, 4.2vw, 18px)' }}
        data-testid="quote-body-input"
      />

      {/* Signature preview */}
      <div className="text-right pr-3 mt-2">
        <p
          className="font-['Cormorant_Garamond'] italic text-[11px]"
          style={{ color: 'rgba(82,46,8,0.7)' }}
        >
          — to be sealed by
        </p>
        <p
          className="font-['Cormorant_Garamond'] font-bold tracking-wide"
          style={{ color: INK, fontSize: 'clamp(18px, 4.8vw, 22px)' }}
        >
          {empireName}
        </p>
      </div>

      {/* Char counter */}
      <div className="text-[10px] font-['Outfit'] text-amber-900/50 text-right mt-1">
        {value.length}/500
      </div>

      {/* Seal button */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting || !value.trim()}
        data-testid="quote-submit"
        className="mt-3 w-full py-2.5 rounded-full font-['Outfit'] uppercase tracking-[0.18em] text-xs disabled:opacity-40 active:scale-95 transition-transform"
        style={{
          background: `linear-gradient(180deg, ${GOLD_LIGHT}, ${GOLD} 60%, #8B6B1F)`,
          color: '#1a0f02',
          boxShadow: '0 4px 14px rgba(168,107,31,0.55)',
        }}
      >
        {submitting ? 'Sealing...' : 'Seal & Inscribe'}
      </button>
    </div>
  </PageFrame>
);

/* Read-only sealed quote page */
const QuotePage = ({ author, body, lesson, date, isOwner, onDelete, signatureUrl }) => (
  <PageFrame>
    <div className="relative flex-1 flex flex-col px-5 sm:px-7 pt-7 pb-5">
      {isOwner && onDelete && (
        <button
          onClick={onDelete}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.1)' }}
          data-testid="quote-delete-btn"
        >
          <Trash2 size={13} style={{ color: '#5a1a16' }} />
        </button>
      )}

      <p
        className="font-['Cormorant_Garamond'] uppercase tracking-[0.32em] text-[10px] text-center"
        style={{ color: 'rgba(82,46,8,0.7)' }}
      >
        ~ A Royal Decree ~
      </p>
      <div
        className="mx-auto mt-2 mb-4 w-20 h-[1px]"
        style={{ background: GOLD }}
      />

      {lesson && (
        <h3
          className="font-['Cormorant_Garamond'] font-semibold text-center mb-3"
          style={{ color: INK, fontSize: 'clamp(20px, 5.5vw, 28px)' }}
        >
          {lesson}
        </h3>
      )}

      <p
        className="flex-1 font-['Cormorant_Garamond'] italic leading-relaxed text-center px-2"
        style={{ color: '#2a0e0a', fontSize: 'clamp(16px, 4.5vw, 20px)' }}
      >
        &ldquo;{body}&rdquo;
      </p>

      <div
        className="mx-auto my-4 w-24 h-[1px]"
        style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}
      />

      {/* Centered author block + wax seal below the user name */}
      <div className="text-center">
        <p
          className="font-['Cormorant_Garamond'] italic text-[11px]"
          style={{ color: 'rgba(82,46,8,0.7)' }}
        >
          — sealed by
        </p>
        <p
          className="font-['Cormorant_Garamond'] font-bold tracking-wide"
          style={{ color: INK, fontSize: 'clamp(18px, 4.8vw, 22px)' }}
        >
          {author}
        </p>
        {date && (
          <p
            className="font-['Outfit'] text-[10px] mt-0.5"
            style={{ color: 'rgba(58,20,16,0.55)' }}
          >
            {new Date(date).toLocaleDateString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}
          </p>
        )}
        {/* Wax seal placed BELOW the username, centered */}
        <div className="mt-2 flex justify-center">
          <WaxSeal signatureUrl={signatureUrl} size={48} />
        </div>
      </div>
    </div>
  </PageFrame>
);

export const QuoteBook = ({ onClose, readOnlyData = null }) => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState(readOnlyData?.quotes || []);
  const [page, setPage] = useState(0); // open on Cover (page 0); page 1 = Compose; page 2..N = previous quotes
  const [direction, setDirection] = useState(1); // -1 backward, +1 forward
  const [body, setBody] = useState('');
  const [lesson, setLesson] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sealedFlash, setSealedFlash] = useState(false);
  const isReadOnly = !!readOnlyData;
  const empireName = isReadOnly
    ? (readOnlyData.ownerName || 'Sovereign')
    : (user?.nickname || user?.email?.split('@')[0] || 'Anonymous');

  const load = useCallback(async () => {
    if (isReadOnly) return; // read-only mode uses provided quotes as-is
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quotes/feed?limit=200`);
      setQuotes(Array.isArray(data) ? data : []);
    } catch (e) {
      setQuotes([]);
    }
  }, [isReadOnly]);

  useEffect(() => { load(); }, [load]);

  // Read-only book layout: 0=cover, 1..N=quotes. If the visitor has zero
  // quotes we still expose page 1 as an empty parchment so the book turns
  // like the owner's book (which flips from cover → compose page even
  // when empty). Owner: 0=cover, 1=compose, 2..N=quotes.
  const totalPages = isReadOnly
    ? (quotes.length > 0 ? 1 + quotes.length : 2)
    : 2 + quotes.length;
  const firstQuotePage = isReadOnly ? 1 : 2;
  const goPrev = () => {
    setDirection(-1);
    setPage((p) => Math.max(0, p - 1));
  };
  const goNext = () => {
    setDirection(1);
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };

  /* Touch swipe — REAL BOOK gesture:
       swipe LEFT→RIGHT (dx>0) → PREVIOUS page (back to cover)
       swipe RIGHT→LEFT (dx<0) → NEXT page */
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    const sx = touchStartX.current; const sy = touchStartY.current;
    touchStartX.current = null; touchStartY.current = null;
    if (sx == null) return;
    const dx = e.changedTouches[0].clientX - sx;
    const dy = e.changedTouches[0].clientY - (sy ?? 0);
    if (Math.abs(dx) < 60) return;
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) goNext(); else goPrev();
  };

  const submit = async () => {
    const text = (body || '').trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('category', 'quote');
      fd.append('note', text);
      fd.append('media_type', 'text');
      fd.append('vibe_date', lesson.slice(0, 80));
      await axios.post(`${BACKEND_URL}/api/memories`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBody('');
      setLesson('');
      await load();
      // Confirm the seal with a clear ceremony, then auto-flip to the
      // newly-sealed page so the user sees their decree on parchment.
      setSealedFlash(true);
      setTimeout(() => {
        setSealedFlash(false);
        setDirection(1);
        setPage(2); // newest quote sits at index 2 (0=cover, 1=compose)
      }, 1300);
    } catch (e) {
      window.alert('Failed to seal quote — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (isReadOnly) return;
    if (!window.confirm('Remove this royal decree?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/memories/${id}`);
      await load();
      // Stay on current page (or shift to compose if we were viewing the deleted)
      setPage((p) => (p > 1 ? Math.max(1, p - 1) : p));
    } catch (e) { /* ignore */ }
  };

  /* Build the current page node */
  let pageNode;
  if (page === 0) {
    pageNode = <CoverPage empireName={empireName} profilePicture={isReadOnly ? readOnlyData.ownerProfilePicture : user?.profile_picture} readOnly={isReadOnly} />;
  } else if (!isReadOnly && page === 1) {
    pageNode = (
      <ComposePage
        empireName={empireName}
        onSubmit={submit}
        submitting={submitting}
        value={body}
        onChange={setBody}
        lesson={lesson}
        onLessonChange={setLesson}
      />
    );
  } else if (isReadOnly && quotes.length === 0) {
    // Read-only empty state: the sovereign has sealed no decrees yet.
    // Renders as a real book page (same PageFrame) so the cover turns
    // into it exactly like the owner's cover → compose flow.
    pageNode = (
      <PageFrame side="right">
        <div className="absolute inset-6 flex flex-col items-center justify-center text-center">
          <Feather size={38} style={{ color: 'rgba(90,45,10,0.35)' }} />
          <p className="font-['Cormorant_Garamond'] italic mt-4"
            style={{ color: 'rgba(60,30,5,0.62)', fontSize: 20, lineHeight: 1.25 }}>
            no decrees have been inscribed
          </p>
          <p className="font-['Outfit'] uppercase tracking-[0.24em] mt-3"
            style={{ color: 'rgba(60,30,5,0.4)', fontSize: 9 }}>
            the parchment awaits
          </p>
        </div>
      </PageFrame>
    );
  } else {
    const q = quotes[page - firstQuotePage];
    if (q) {
      pageNode = (
        <QuotePage
          author={q.user_nickname || q.user_email?.split('@')[0] || empireName}
          body={q.note || 'A timeless lesson.'}
          lesson={q.vibe_date || ''}
          date={q.created_at}
          isOwner={!isReadOnly && q.user_email === user?.email}
          onDelete={() => remove(q.id)}
          signatureUrl={
            isReadOnly
              ? (q.signature_data_url || null)
              : (q.user_email === user?.email ? user?.signature_data_url : (q.signature_data_url || null))
          }
        />
      );
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col imperium-shell"
      style={{
        background:
          'radial-gradient(ellipse at top, #1a1208 0%, #060403 60%, #000 100%)',
      }}
      data-testid="quote-book"
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{
          paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)',
          borderBottom: '1px solid rgba(212,175,55,0.18)',
        }}
      >
        <BackPill onClick={onClose} accent="#E0474C" testId="quote-close"/>
        <div className="flex flex-col items-center">
          <h2
            className="font-['Cormorant_Garamond'] text-xl font-light tracking-wide flex items-center gap-2"
            style={{ color: GOLD_LIGHT }}
          >
            <Feather size={16} />
            {isReadOnly ? `${empireName}'s Royal Quotes` : 'Royal Quote Book'}
          </h2>
          <p className="font-['Outfit'] text-[10px] tracking-[0.18em] text-white/30 mt-0.5">
            {page === 0
              ? 'COVER'
              : (!isReadOnly && page === 1)
                ? 'NEW DECREE'
                : `PAGE ${isReadOnly ? page : page - 1} / ${quotes.length}`}
          </p>
        </div>
        <div className="w-6" />
      </div>

      {/* Book stage — flexes to fill freed space; swipe RIGHT → next, swipe LEFT → prev (→ cover) */}
      <div className="flex-1 flex items-center justify-center px-2 pb-3 min-h-0"
        style={{ paddingBottom: 'calc(12px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div
          className="relative w-full max-w-[480px] h-full"
          style={{ perspective: '1800px' }}
        >
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={page}
              custom={direction}
              initial={{ opacity: 0, rotateY: direction > 0 ? 170 : -170 }}
              animate={{ opacity: 1, rotateY: 0 }}
              exit={
                direction > 0
                  ? { rotateY: -170, opacity: 0 }
                  : { rotateY: 170,  opacity: 0 }
              }
              transition={{
                rotateY: { duration: 0.7, ease: [0.55, 0.05, 0.25, 0.98] },
                opacity: { duration: 0.55 },
              }}
              style={{
                position: 'absolute', inset: 0,
                transformOrigin: direction > 0 ? 'left center' : 'right center',
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                boxShadow: '0 8px 28px rgba(0,0,0,0.6), inset 0 0 40px rgba(0,0,0,0.25)',
              }}
            >
              {pageNode}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Sealed ceremony overlay — confirms the quote was saved + flips to it */}
      <AnimatePresence>
        {sealedFlash && (
          <motion.div
            key="sealed-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(168,107,31,0.32) 0%, rgba(0,0,0,0.92) 60%, #000 100%)',
              backdropFilter: 'blur(4px)',
            }}
            data-testid="quote-sealed-overlay"
          >
            <motion.div
              initial={{ scale: 0.4, rotate: -8, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 220, damping: 14 }}
            >
              <WaxSeal signatureUrl={user?.signature_data_url} size={120} />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.4 }}
              className="mt-5 font-['Cormorant_Garamond'] italic text-2xl"
              style={{ color: GOLD_LIGHT }}
            >
              Sealed.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="font-['Outfit'] uppercase tracking-[0.3em] text-[10px] mt-1"
              style={{ color: GOLD }}
            >
              Inscribed in your Royal Quote Book
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
